import api from "./NetworkInterceptor";
import ConstantsApi from "../constants/ConstantsApi";
import ConstantsTexts from "../constants/ConstantsTexts";
import UtilsUnit from "../utils/UtilsUnit";

class ApiService {
  // Dshboard
  static async getDashboardData(modelVersion) {
    try {
      const response = await api.post(ConstantsApi.GET_DASHBOARD_DATA, { modelVersion });
      return response.data;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_153);
      throw error;
    }
  }

  static async getDashboardDataComparison(modelVersion1, modelVersion2) {
    try {
      const response = await api.post(ConstantsApi.GET_DASHBOARD_DATA_COMPARISON, { modelVersion1, modelVersion2 });
      return response.data;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_153);
      throw error;
    }
  }

  static async exportDashboardExcelData(data) {
    try {
      const response = await api.post(ConstantsApi.EXPORT_DASHBOARD_EXCEL_DATA, data);
      return response.data;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_154);
      throw error;
    }
  }

  static async exportDashboardExcelDataComparison(data) {
    try {
      const response = await api.post(ConstantsApi.EXPORT_DASHBOARD_EXCEL_DATA_COMPARISON, data);
      return response.data;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_154);
      throw error;
    }
  }

  // Filter
  static async getDataForFiltering() {
    try {
      const response = await api.get(ConstantsApi.GET_DATA_FOR_FILTERING);
      return response.data;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_73);
      throw error;
    }
  }

  static async getPendingChangeRequestsCount() {
    try {
      const response = await api.get(ConstantsApi.GET_PENDING_CHANGE_REQUESTS_COUNT);
      const count = response?.data?.pendingRequestsCount ?? response?.data?.count ?? 0;
      const parsed = Number(count);
      return Number.isFinite(parsed) ? parsed : 0;
    } catch (error) {
      return 0;
    }
  }

  static async getPendingChangeRequests() {
    try {
      const response = await api.get(ConstantsApi.GET_PENDING_CHANGE_REQUESTS);
      return Array.isArray(response?.data) ? response.data : [];
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_259);
      return [];
    }
  }

  // Model
  static async getModelData(modelVersion) {
    try {
      const response = await api.post(ConstantsApi.GET_MODEL_DATA, { modelVersion });
      return response.data;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_74);
      throw error;
    }
  }

  static async makeModelAsZero(data) {
    try {
      const response = await api.post(ConstantsApi.MAKE_MODEL_AS_ZERO, data);
      return response;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_175);
      throw error;
    }
  }

  static async updateModelData(data) {
    try {
      const response = await api.post(ConstantsApi.UPDATE_MODEL_DATA, data);
      return response;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_77);
      throw error;
    }
  }

  static async deleteModelVersion(data) {
    try {
      const response = await api.post(ConstantsApi.DELETE_MODEL_VERSION, data);
      return response;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_210);
      throw error;
    }
  }

  static async freezeModelVersion(data) {
    try {
      const response = await api.post(ConstantsApi.FREEZE_MODEL_VERSION, data);
      return response;
    } catch (error) {
      const status = error?.response?.status;
      const apiMessage = error?.response?.data?.message || "";
      if (status === 400 && /24 hours/i.test(apiMessage)) {
        UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_247);
      } else {
        UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_248);
      }
      throw error;
    }
  }

  static async deleteModelOptionValue(data) {
    try {
      const response = await api.post(ConstantsApi.DELETE_MODEL_OPTION_VALUE, data);
      return response;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_228);
      throw error;
    }
  }

  static async editModelOptionValue(data) {
    try {
      const response = await api.post(ConstantsApi.EDIT_MODEL_OPTION_VALUE, data);
      return response;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_229);
      throw error;
    }
  }

  static async addModelOptionValue(data) {
    try {
      const response = await api.post(ConstantsApi.ADD_MODEL_OPTION_VALUE, data);
      return response;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_230);
      throw error;
    }
  }

  static async getModelOptionsValues() {
    try {
      const response = await api.post(ConstantsApi.GET_MODEL_OPTIONS_VALUES);
      return response;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_231);
      throw error;
    }
  }

  // Terminal
  static async getTerminalData(terminalID, modelVersion) {
    try {
      const response = await api.post(ConstantsApi.GET_TERMINAL_DATA, { terminalID, modelVersion });
      return response.data;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_75);
      throw error;
    }
  }

  /** Batch GeoJSON for map polygons — no user toast (background load). */
  static async getTerminalsGeoJSON(terminalIDs, modelVersion) {
    const response = await api.post(ConstantsApi.GET_TERMINALS_GEOJSON, {
      terminalIDs,
      modelVersion,
    });
    return response.data;
  }

  static async makeTerminalAsZero(data) {
    try {
      const response = await api.post(ConstantsApi.MAKE_TERMINAL_AS_ZERO, data);
      return response;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_176);
      throw error;
    }
  }

  static async updateTerminalData(data) {
    try {
      const response = await api.post(ConstantsApi.UPDATE_TERMINAL_DATA, data);
      return response;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_76);
      throw error;
    }
  }

  static async createTerminalSubjectiveChangeRequest(data) {
    try {
      const response = await api.post(ConstantsApi.CREATE_TERMINAL_SUBJECTIVE_CHANGE_REQUEST, data);
      return response;
    } catch (error) {
      const status = error?.response?.status;
      if (status !== 409) {
        UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_261);
      }
      throw error;
    }
  }

  static async approveTerminalSubjectiveChangeRequest(data) {
    try {
      const response = await api.post(ConstantsApi.APPROVE_TERMINAL_SUBJECTIVE_CHANGE_REQUEST, data);
      return response;
    } catch (error) {
      const serverMessage = error?.response?.data?.error || error?.response?.data?.message;
      UtilsUnit.checkResponseStatus(serverMessage || ConstantsTexts.CODE_280);
      throw error;
    }
  }

  static async rejectTerminalSubjectiveChangeRequest(data) {
    try {
      const response = await api.post(ConstantsApi.REJECT_TERMINAL_SUBJECTIVE_CHANGE_REQUEST, data);
      return response;
    } catch (error) {
      const serverMessage = error?.response?.data?.error || error?.response?.data?.message;
      UtilsUnit.checkResponseStatus(serverMessage || ConstantsTexts.CODE_282);
      throw error;
    }
  }

  static async deleteTerminal(data) {
    try {
      const response = await api.post(ConstantsApi.DELETE_TERMINAL, data);
      return response;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_204);
      throw error;
    }
  }

  // User
  static async getUserData(code) {
    try {
      const response = await api.get(ConstantsApi.GET_USER_DATA, {
        headers: {
          Authorization: code,
        },
      });
      return response.data;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_84);
      throw error;
    }
  }

  static async refreshToken() {
    try {
      const accessToken = UtilsUnit.getAccessToken();
      const refreshToken = UtilsUnit.getRefreshToken();
      const response = await api.post(ConstantsApi.REFRESH_TOKEN, { accessToken, refreshToken });
      const newAccessToken = response.data.AccessToken;
      UtilsUnit.handleSetTokens({ access_token: newAccessToken });
      return newAccessToken;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_131);
      throw error;
    }
  }

  static async getAllUsers() {
    try {
      const response = await api.post(ConstantsApi.GET_ALL_USERS);
      return response.data;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_220);
      throw error;
    }
  }

  static async updateUserData(data) {
    try {
      const response = await api.post(ConstantsApi.UPDATE_USER_DATA, data);
      return response;
    } catch (error) {
      UtilsUnit.checkResponseStatus(ConstantsTexts.CODE_223);
      throw error;
    }
  }
}

export default ApiService;
